import { Accordion, Alert, Avatar, Button, Card, Center, Text, Container, useMantineTheme, Tooltip, Badge, Loader, Group, Stack, Modal, Title } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from "../../auth/SessionContext";
import { IconDeviceDesktop, IconInfinity, IconArrowUpRight } from "@tabler/icons-react";
import { format, parseISO } from 'date-fns';
import { DEBUG } from "../..";
import { AppRefData, RefKeyMonthly, LoadRefKeyDataAsync, RefKeys } from './RefReport';
import { buildSampleOrders } from "../../scripts/SampleOrders";
import { appsFor, subFeaturesFor, isLmuTvKey } from "../../scripts/TenantApps";
import { SeatManager, License } from "../SubscriptionList/SeatManager";
import { EditionManager, EditionTarget } from "../SubscriptionList/EditionManager";
import { useWizard } from "../BuyWizard/WizardContext";
import { Sub, Status, loadSubs, UpgradePanel, KeyReveal } from "../SubscriptionList/SubscriptionList";
import { Edition, Duration } from "../../Settings";
import classes from "./OrderList.module.css";

export interface OrderData {
  id: number;
  ident: string;
  owner: string;
  discordId: string | null;
  created: string;
  features: number;
  refunded: boolean;
  steamId: number;
  strSteamId: string;
  steamIdBindingTime: string;
  affiliate: string;
  validUntil?: string | null;
  featureGrants?: { features: number; validUntil: string; type?: number | string }[];
  subscriptionValidUntil?: string | null;
  lastSteamReset?: string | null;
  activationLimit?: number | null;
  steamBindings?: { steamId: number; status: number | string; lastUnbound?: string | null }[];
}

// Effective feature bitmask = base features OR every currently-active grant.
export function effectiveFeatures(od: OrderData): number {
  let mask = od.features ?? 0;
  const now = Date.now();
  for (const g of od.featureGrants ?? []) {
    if (g?.validUntil && new Date(g.validUntil).getTime() > now) mask |= g.features ?? 0;
  }
  return mask;
}

// All features the key EVER carried (base OR any grant, ignoring validity) — used for app
// membership + icons so an expired/inactive key still shows which app it belonged to.
function allFeatures(od: OrderData): number {
  let mask = od.features ?? 0;
  for (const g of od.featureGrants ?? []) mask |= g.features ?? 0;
  return mask;
}

// Inactive = refunded OR no current access (expired grant, no permanent base).
const isInactive = (od: OrderData) => od.refunded || effectiveFeatures(od) === 0;

type KeyType = "lifetime" | "subscription" | "trial" | "timed";
function keyType(od: OrderData): KeyType {
  const base = od.features ?? 0;
  if (od.subscriptionValidUntil) return "subscription";
  if (base !== 0 && !od.validUntil) return "lifetime";
  if (base === 0 && (od.featureGrants?.length ?? 0) > 0) return "trial";
  return "timed";
}
function effectiveEnd(od: OrderData): string | null {
  if (od.subscriptionValidUntil) return od.subscriptionValidUntil;
  const ends = [od.validUntil, ...(od.featureGrants ?? []).map((g) => g.validUntil)].filter(Boolean) as string[];
  return ends.length ? ends.slice().sort().slice(-1)[0] : null;
}
const isActiveBinding = (b: { status: number | string }) =>
  b.status === 1 || b.status === "1" || String(b.status) === "Active";
const seatsTotal = (od: OrderData) => Math.max(1, od.activationLimit ?? 1);
const seatsUsed = (od: OrderData) => (od.steamBindings ?? []).filter(isActiveBinding).length;

export async function LoadOrderDataAsync(): Promise<OrderData[]> {
  return fetch('/dyn/lmutv/get-orders.php', { method: 'GET', credentials: 'include' })
    .then(response => response.json())
    .then(data => Array.isArray(data) ? data : [])
    .catch(error => { if (DEBUG) console.error('Error:', error); return []; });
}

function formatDate(dateString: string): string {
  try { return format(parseISO(dateString), 'dd.MM.yyyy'); } catch { return dateString; }
}

const monthNames = [
  '', 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

interface MonthlySummary { count: number; refunds: number; revenue: number; }

// ── One license card: apps, type/validity, key, seats + billing (if a subscription) ──
function LicenseCard({ data, sub, readOnly, onSeats, onUpgrade, onCancel, onEdition, onUpgradeTrial }: {
  data: OrderData;
  sub?: Sub;
  readOnly?: boolean; // inactive (refunded) keys are shown read-only, no management
  onSeats: (l: License) => void;
  onUpgrade: (s: Sub) => void;
  onCancel: (s: Sub) => void;
  onEdition: (e: EditionTarget) => void;
  onUpgradeTrial: (keyIdent: string, pcs: number) => void; // trial → Abo/Lifetime, keeps the key
}) {
  const { t } = useTranslation("translation");
  const eff = allFeatures(data); // apps/edition/icons from all-time features (show expired keys' apps)
  const expired = !data.refunded && effectiveFeatures(data) === 0;
  const apps = appsFor(eff);
  const subFeats = subFeaturesFor(eff);
  const type = keyType(data);
  const total = seatsTotal(data);
  const used = seatsUsed(data);
  const end = effectiveEnd(data);
  const validity = type === "lifetime" ? t("account.licenses.unlimited") : (end ? formatDate(end) : t("account.licenses.unlimited"));
  const typeColor = type === "lifetime" ? "lmuTVColors" : type === "subscription" ? "green" : type === "trial" ? "yellow" : "gray";
  const edition: Edition = (eff & 1024) ? "pro" : "standard";

  // Self-service seat/edition changes exist ONLY for lifetime keys (increase/upgrade).
  // Subscriptions keep cancel + upgrade-to-lifetime + device release only; seat/edition
  // changes on a subscription go through support or a new subscription.
  const activeSub = sub && !sub.cancelled;
  const lmu = isLmuTvKey(eff);
  const canCount = lmu && type === "lifetime";                       // lifetime: increase seats
  const curEdition: Edition = sub ? sub.edition : edition;
  const canEdition = lmu && type === "lifetime" && curEdition === "standard"; // lifetime: Std→Pro

  const license: License = sub
    ? {
        key_ident: data.ident, edition: sub.edition, type: "subscription", currentQty: total,
        canChangeCount: false, subscription_id: sub.subscription_id, // device release only
        duration: sub.duration as Duration, next_billing_date: sub.next_billing_date,
      }
    : {
        key_ident: data.ident, edition, type: type === "lifetime" ? "lifetime" : "subscription",
        currentQty: total, canChangeCount: isLmuTvKey(eff) && type === "lifetime",
      };

  return (
    <Card withBorder radius="md" p="md" style={{ opacity: data.refunded ? 0.55 : 1 }}>
      <Stack gap="sm">
        <Group justify="space-between" wrap="wrap" gap="xs">
          <Group gap="xs">
            {apps.length > 0
              ? apps.map((a) => (
                  <Tooltip key={a.bit} label={a.name} withArrow>
                    <Avatar src={a.logo} size={32} radius="sm" alt={a.name} />
                  </Tooltip>
                ))
              : <Text size="sm" c="dimmed">—</Text>}
          </Group>
          <Group gap={6}>
            <Badge color={sub?.cancelled ? "gray" : typeColor} variant="light" leftSection={type === "lifetime" ? <IconInfinity size={12} /> : undefined}>
              {sub?.cancelled ? t("account.subscriptions.cancelledBadge") : t(`account.licenses.type.${type}`)}
            </Badge>
            {subFeats.map((s) => <Badge key={s} size="sm" variant="outline" color="gray">{s}</Badge>)}
            {data.refunded && <Badge color="red" variant="light">{t("account.licenses.refunded")}</Badge>}
            {expired && <Badge color="gray" variant="light">{t("account.licenses.expired")}</Badge>}
          </Group>
        </Group>

        {data.ident && <KeyReveal ident={data.ident} label={t("orders.header.key")} />}

        <Group justify="space-between" wrap="wrap">
          <Text size="sm"><Text span c="dimmed">{t("account.licenses.validUntil")}: </Text>{validity}</Text>
          <Group gap={6} wrap="nowrap">
            <IconDeviceDesktop size={14} />
            <Text size="sm">{used} / {total} {t("cart.pcs")}</Text>
          </Group>
        </Group>

        {sub?.pending_quantity ? (
          <Text size="xs" c="lmuTVColors.4">{t("account.seats.pendingDowngrade", { n: sub.pending_quantity })}</Text>
        ) : null}

        {!readOnly && (
        <Group gap="xs">
          <Button size="xs" variant="light" color="lmuTVColors" leftSection={<IconDeviceDesktop size={14} />} onClick={() => onSeats(license)}>
            {canCount ? t("account.seats.manageCta") : t("account.licenses.manageDevices")}
          </Button>
          {type === "trial" && (
            <Button size="xs" color="lmuTVColors" leftSection={<IconArrowUpRight size={14} />} onClick={() => onUpgradeTrial(data.ident, total)}>
              {t("account.licenses.upgradeTrial")}
            </Button>
          )}
          {canEdition && (
            <Button size="xs" variant="light" color="gray" onClick={() => onEdition({
              key_ident: data.ident,
              edition: curEdition,
              type: sub ? "subscription" : (type === "lifetime" ? "lifetime" : "subscription"),
              seats: total,
              subscription_id: sub?.subscription_id,
              duration: sub ? (sub.duration as Duration) : undefined,
              next_billing_date: sub?.next_billing_date ?? null,
            })}>
              {t("account.edition.toPro")}
            </Button>
          )}
          {activeSub && (
            <>
              <Button size="xs" color="lmuTVColors" leftSection={<IconInfinity size={14} />} onClick={() => onUpgrade(sub!)}>
                {t("account.subscriptions.upgradeCta")}
              </Button>
              <Button size="xs" variant="subtle" color="red" onClick={() => onCancel(sub!)}>
                {t("account.subscriptions.cancelCta")}
              </Button>
            </>
          )}
        </Group>
        )}
      </Stack>
    </Card>
  );
}

export function OrderList() {
  const navigate = useNavigate();
  const { t } = useTranslation("translation");
  const handleRedirect = () => navigate('/');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orderData, setOrderData] = useState<OrderData[]>([]);
  const [subs, setSubs] = useState<Sub[]>([]);
  const [refData, setRefData] = useState<AppRefData>({});
  const [seatTarget, setSeatTarget] = useState<License | null>(null);
  const [editionTarget, setEditionTarget] = useState<EditionTarget | null>(null);
  const [upgradeTarget, setUpgradeTarget] = useState<Sub | null>(null);
  const [upgradeStatus, setUpgradeStatus] = useState<Status>("idle");
  const [cancelTarget, setCancelTarget] = useState<Sub | null>(null);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelErr, setCancelErr] = useState(false);
  const { loadSessionDataAsync } = useSession();
  const theme = useMantineTheme();
  const wizard = useWizard();

  const reload = async () => {
    const [o, s] = await Promise.all([LoadOrderDataAsync(), loadSubs()]);
    setOrderData(o.filter((od) => appsFor(allFeatures(od)).length > 0));
    setSubs(s);
  };

  const doCancel = async () => {
    if (!cancelTarget) return;
    setCancelBusy(true); setCancelErr(false);
    try {
      const fd = new FormData();
      fd.append("subscription_id", cancelTarget.subscription_id);
      const r = await fetch("/dyn/cancel-subscription.php", { method: "POST", body: fd, credentials: "include" });
      if ((await r.json()).success) { setCancelTarget(null); await reload(); }
      else setCancelErr(true);
    } catch (e) { if (DEBUG) console.error("cancel", e); setCancelErr(true); }
    finally { setCancelBusy(false); }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await loadSessionDataAsync();
        if (!result) { handleRedirect(); return; }
        const [orders, s] = await Promise.all([LoadOrderDataAsync(), loadSubs()]);
        LoadRefKeyDataAsync().then(setRefData);
        setOrderData(orders.filter((od) => appsFor(allFeatures(od)).length > 0));
        setSubs(s);
        setLoading(false);
      } catch (err) {
        if (DEBUG) console.error('Error:', err);
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
        setLoading(false);
      }
    };
    if (process.env.NODE_ENV !== 'development') {
      fetchData();
    } else {
      setOrderData(buildSampleOrders());
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <Center style={{ height: '40vh' }}><Loader color="lmuTVColors" size="xl" /></Center>;
  }
  if (error) return <div>Error: {error}</div>;

  // Match each key to its subscription billing row (if any) by key_ident.
  const subByIdent: Record<string, Sub> = {};
  for (const s of subs) if (s.key_ident) subByIdent[s.key_ident.toLowerCase()] = s;

  // Active vs inactive (refunded / expired / no access) — inactive shown read-only.
  const active = orderData.filter((od) => !isInactive(od));
  const inactive = orderData.filter((od) => isInactive(od));

  const groupByYearMonth = (): Record<number, Record<number, { appName: string; data: RefKeyMonthly }[]>> => {
    const grouped: Record<number, Record<number, { appName: string; data: RefKeyMonthly }[]>> = {};
    for (const [appName, entries] of Object.entries(refData)) {
      for (const entry of entries) {
        const { year, month } = entry;
        if (!grouped[year]) grouped[year] = {};
        if (!grouped[year][month]) grouped[year][month] = [];
        grouped[year][month].push({ appName, data: entry });
      }
    }
    return grouped;
  };
  const grouped = groupByYearMonth();

  return (
    <Container size="100%" style={{ maxWidth: theme.breakpoints.lg, width: '100%' }}>
      <Accordion multiple variant="separated" defaultValue={['orderList']}>
        <Accordion.Item key="orderList" value="orderList">
          <Accordion.Control>{t("account.licenses.activeTitle")}</Accordion.Control>
          <Accordion.Panel>
            {active.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="md">{t("account.licenses.empty")}</Text>
            ) : (
              <Stack gap="sm">
                {active.map((data, index) => (
                  <LicenseCard
                    key={`${data.ident}-${index}`}
                    data={data}
                    sub={subByIdent[(data.ident || "").toLowerCase()]}
                    onSeats={setSeatTarget}
                    onEdition={setEditionTarget}
                    onUpgrade={(s) => { setUpgradeStatus("idle"); setUpgradeTarget(s); }}
                    onCancel={(s) => { setCancelErr(false); setCancelTarget(s); }}
                    onUpgradeTrial={(id, count) => wizard?.openWizard({ pcs: count, upgradeKeyIdent: id })}
                  />
                ))}
              </Stack>
            )}
          </Accordion.Panel>
        </Accordion.Item>

        {inactive.length > 0 && (
          <Accordion.Item key="inactiveList" value="inactiveList">
            <Accordion.Control>{t("account.licenses.inactiveTitle")}</Accordion.Control>
            <Accordion.Panel>
              <Stack gap="sm">
                {inactive.map((data, index) => (
                  <LicenseCard
                    key={`${data.ident}-inactive-${index}`}
                    data={data}
                    readOnly
                    onSeats={() => {}}
                    onEdition={() => {}}
                    onUpgrade={() => {}}
                    onCancel={() => {}}
                    onUpgradeTrial={() => {}}
                  />
                ))}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {Object.keys(grouped).length > 0 && (
          <Accordion.Item key="affiliateReport" value="affiliateReport">
            <Accordion.Control>Affiliate Report</Accordion.Control>
            <Accordion.Panel>
              <Accordion multiple variant="separated">
                {Object.entries(grouped).sort(([a], [b]) => +b - +a).map(([year, months]) => {
                  const yearSummary = Object.values(months).flat().reduce((acc, { data }) => {
                    data.refKeys.forEach(rk => { acc.count += rk.count; acc.refunds += rk.refunds; acc.revenue += rk.revenue * rk.share; });
                    return acc;
                  }, { count: 0, refunds: 0, revenue: 0 } as MonthlySummary);

                  return (
                    <Accordion.Item key={`year-${year}`} value={`year-${year}`}>
                      <Accordion.Control>Jahr {year} – {yearSummary.revenue.toFixed(2)} € - {yearSummary.count} Keys</Accordion.Control>
                      <Accordion.Panel>
                        <Accordion multiple variant="separated">
                          {Object.entries(months).sort(([a], [b]) => +b - +a).map(([month, entries]) => {
                            const monthSummary = entries.flatMap(e => e.data.refKeys).reduce((acc, rk) => {
                              acc.count += rk.count; acc.refunds += rk.refunds; acc.revenue += rk.revenue * rk.share;
                              return acc;
                            }, { count: 0, refunds: 0, revenue: 0 } as MonthlySummary);

                            return (
                              <Accordion.Item key={`month-${year}-${month}`} value={`month-${year}-${month}`}>
                                <Accordion.Control>{monthNames[+month]} {year} – {monthSummary.revenue.toFixed(2)} € - {monthSummary.count} Keys</Accordion.Control>
                                <Accordion.Panel>
                                  <div>
                                    {Object.entries(
                                      entries.reduce((acc, { appName, data }) => {
                                        if (!acc[appName]) acc[appName] = [];
                                        acc[appName].push(...data.refKeys);
                                        return acc;
                                      }, {} as Record<string, RefKeys[]>)
                                    ).map(([appName, refKeys]) => {
                                      const total = refKeys.reduce((acc, rk) => {
                                        acc.count += rk.count; acc.refunds += rk.refunds; acc.revenue += rk.revenue * rk.share;
                                        return acc;
                                      }, { count: 0, refunds: 0, revenue: 0 } as MonthlySummary);

                                      return (
                                        <div key={appName} style={{ marginBottom: 12, borderBottom: '1px solid #eee', paddingBottom: 6 }}>
                                          <strong>{appName}</strong><br />
                                          Keys: {total.count}, Refunds: {total.refunds}, Revenue: {total.revenue.toFixed(2)} €
                                          <div style={{ marginTop: 6, marginLeft: 12 }}>
                                            {refKeys.map((rk, i) => (
                                              <div key={i} style={{ fontSize: '0.9em', padding: '2px 0' }}>
                                                <strong>{rk.refName}</strong>: {rk.count} Keys, {rk.refunds} Refunds, {(rk.revenue * rk.share).toFixed(2)} €
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </Accordion.Panel>
                              </Accordion.Item>
                            );
                          })}
                        </Accordion>
                      </Accordion.Panel>
                    </Accordion.Item>
                  );
                })}
              </Accordion>
            </Accordion.Panel>
          </Accordion.Item>
        )}
      </Accordion>

      {/* Upgrade-to-lifetime */}
      <Modal
        opened={!!upgradeTarget}
        onClose={() => { if (upgradeStatus !== "saving") { setUpgradeTarget(null); if (upgradeStatus === "success") reload(); } }}
        title={<Title order={4}>{t("account.subscriptions.upgradeTitle")}</Title>}
        size="md"
        centered
      >
        {upgradeTarget && (
          <UpgradePanel
            sub={upgradeTarget}
            status={upgradeStatus}
            setStatus={setUpgradeStatus}
            onDone={() => { setUpgradeTarget(null); reload(); }}
          />
        )}
      </Modal>

      {/* Cancel subscription */}
      <Modal
        opened={!!cancelTarget}
        onClose={() => { if (!cancelBusy) setCancelTarget(null); }}
        title={<Title order={4}>{t("account.subscriptions.cancelTitle")}</Title>}
        size="md"
        centered
      >
        {cancelTarget && (
          <Stack gap="md">
            <Text size="sm" c="dimmed">{t("account.subscriptions.cancelIntro")}</Text>
            <Group justify="space-between">
              <Text fw={600}>{cancelTarget.plan}</Text>
              {cancelTarget.next_billing_date && (
                <Text size="sm" c="dimmed">{t("account.subscriptions.activeUntil")}: {cancelTarget.next_billing_date}</Text>
              )}
            </Group>
            {cancelErr && <Alert color="red" radius="md">{t("account.subscriptions.cancelError")}</Alert>}
            <Group justify="flex-end" gap="sm">
              <Button variant="default" disabled={cancelBusy} onClick={() => setCancelTarget(null)}>{t("account.subscriptions.keep")}</Button>
              <Button color="red" loading={cancelBusy} onClick={doCancel}>{t("account.subscriptions.cancelConfirm")}</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <SeatManager
        license={seatTarget}
        opened={!!seatTarget}
        onClose={() => setSeatTarget(null)}
        onChanged={() => reload()}
      />

      <EditionManager
        target={editionTarget}
        opened={!!editionTarget}
        onClose={() => setEditionTarget(null)}
        onChanged={() => reload()}
      />
    </Container>
  );
}
